import React, { useState } from 'react';
import { useScanWedge } from '../scanWedge';
import { Detail, InboundBox, ManagerMode, ScanReply, boxText } from './inboundApi';

// Scanning happens ON the inbound page, in its one table (Fernando 30.10: "can the work happen in the same table
// instead of switching?"). While a session is open this strip sits fixed at the LOWER THIRD of the screen, full width,
// in the status colour — deliberate, not a footnote — and the page above it scrolls the scanned product to the middle.
// Scans come through the keyboard wedge with no focused field (a focused field off-screen is what makes a browser
// jump to the top on every scan). Save lives here so it is always within reach. -- Claude, 2026-10-30
// 1.11: All scanned is gone (the worker presses Finished on the page, a manager confirms); a piece that needs a check
// (more than ordered, not on the order) gets its "Checked — N came" right here; when the style's box went on a cart
// the strip says so — the black NEW BOX frame is the new number.

const TONE: Record<string, string> = {
  ok: 'bg-green-700', over: 'bg-red-700', extra: 'bg-red-700', unknown: 'bg-red-800', no_box: 'bg-amber-700', ambiguous: 'bg-amber-700',
};

/** The BOX frame's colour (Fernando 30.10: a piece went into Ä by accident — the strip looked like the scan before):
 *  Ä / Ö always purple; the same box as the previous scan → green; a DIFFERENT box (or the first scan) → black with
 *  white text and the word NEW BOX. The strip's own colour keeps its meaning (ok / over / no box). */
export function boxFrame(label: string | null | undefined, prev: string | null | undefined): { cls: string; fresh: boolean } {
  if (!label) return { cls: 'border-white/90', fresh: false };
  if (label === 'Ä' || label === 'Ö') return { cls: 'bg-purple-700 border-purple-200', fresh: false };
  if (prev && label === prev) return { cls: 'bg-green-900 border-white', fresh: false };
  return { cls: 'bg-black border-white', fresh: true };
}

export default function ScanStrip({ last, prevBox, busy, err, note, chip, onScan, onSave, onUndo, onItIsOnOrder, onVerify, onStartReplenish,
                                    station, managerMode, onManagerMode, onManagerModeEnd }: {
  last: ScanReply | null; prevBox?: string | null; busy: boolean; err: string; note?: string; chip: React.ReactNode;
  onScan: (code: string, lineId?: number) => void; onSave: () => void;
  /** "Checked — N came": the count of this line is right (more than ordered, or not on the order) — 1.11 */
  onVerify?: (lineId: number, count: number) => void;
  /** Start replenishing (saves, then a cart tablet takes the boxes) — shown until it was pressed once (1.11) */
  onStartReplenish?: () => void;
  /** take the last scan of this session back (Fernando 30.10: a double scan, a wrong barcode) */
  onUndo?: () => void;
  /** a scan "not on the order" that IS: choose the line, the product gets this barcode */
  onItIsOnOrder?: () => void;
  /** manager mode (stations): a manager's badge once → barcode edits without a PIN per edit, until End */
  station?: boolean; managerMode?: ManagerMode | null; onManagerMode?: () => void; onManagerModeEnd?: () => void;
}) {
  const [typing, setTyping] = useState(false);
  const [code, setCode] = useState('');
  useScanWedge(c => onScan(c), { enabled: !typing, skipFields: true });

  return (
    <div className={`fixed inset-x-0 bottom-0 z-30 h-[33vh] px-6 py-3 border-t border-[#9aa0a6] flex flex-col text-white ${last ? TONE[last.status] || 'bg-gray-700' : 'bg-[#374151]'}`}>
      <div className="flex-none flex items-center gap-3">
        {err ? <div className="text-base font-semibold px-2 py-0.5 rounded bg-black/30">{err}</div>
          : note ? <div className="text-base font-semibold px-2 py-0.5 rounded bg-black/30">{note}</div>
          : <div className="text-sm opacity-70">Scanning</div>}
        <span className="ml-auto">{chip}</span>
        {station && (managerMode
          ? <span className="inline-flex items-center gap-2 text-sm px-2 py-1 rounded-lg bg-amber-300 text-black font-semibold" title="barcode edits at this station need no PIN while this is on">
              Manager mode: {managerMode.username} · {managerMode.minutes_left} min
              <button onClick={onManagerModeEnd} className="underline">End</button>
            </span>
          : onManagerMode && <button onClick={onManagerMode} title="a manager's badge or PIN once → several barcode edits without a PIN each time" className="px-3 py-1.5 bg-black/30 hover:bg-black/50 text-sm rounded-lg">Manager mode</button>)}
        {onUndo && last?.line_id && (
          <button disabled={busy} onClick={onUndo} title="the last scan was a double scan or the wrong barcode — take it back"
            className="px-3 py-1.5 bg-black/30 hover:bg-black/50 text-sm rounded-lg">Undo last scan</button>
        )}
        <button disabled={busy} onClick={onSave} className="px-3 py-1.5 bg-black/30 hover:bg-black/50 text-sm rounded-lg">Save</button>
        {onStartReplenish && (
          <button disabled={busy} onClick={onStartReplenish} title="saves, then a cart tablet takes the boxes — you can go on scanning"
            className="px-4 py-1.5 bg-white/90 hover:bg-white text-blue-900 font-semibold text-sm rounded-lg">Start replenishing →</button>
        )}
      </div>
      {!last && <div className="flex-1 flex items-center justify-center text-gray-200 text-2xl">Scan a piece — its box number shows here.</div>}
      {last && last.status === 'ambiguous' && (
        <div className="flex-1 min-h-0 overflow-y-auto mt-2">
          <div className="text-2xl font-semibold mb-2">Which line?</div>
          <div className="flex flex-wrap gap-2">
            {(last.candidates || []).map((c: any) => (
              <button key={c.line_id} onClick={() => onScan(last.barcode || '', c.line_id)} className="bg-black/20 hover:bg-black/40 rounded px-3 py-2 text-lg">
                Box {c.box_label ?? c.box_no ?? '—'} · {c.name} {c.size} · {c.total_scanned}/{c.qty_ordered}
              </button>
            ))}
          </div>
        </div>
      )}
      {last && last.status !== 'ambiguous' && (
        <div className="flex-1 min-h-0 flex items-center gap-10 pl-[min(11rem,12vw)]">
          {/* the BOX: framed and captioned so nobody reads a size as a box number (Fernando 30.10) */}
          {(() => { const f = boxFrame(last.box_label, prevBox); return (
          <div className={`flex-none border-4 rounded-2xl px-6 py-1 text-center min-w-[11rem] text-white ${f.cls}`}>
            <div className="text-sm font-semibold uppercase tracking-[0.3em] opacity-80">Box</div>
            <div className="font-black leading-none text-[clamp(3.5rem,17vh,8.5rem)]">{last.box_label ?? '—'}</div>
            {f.fresh && <div className="text-sm font-bold uppercase tracking-[0.25em] mt-1">New box</div>}
            {last.left_box && <div className="text-xs font-semibold mt-1 opacity-90">box {last.left_box} is on the cart</div>}
          </div>); })()}
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold uppercase tracking-[0.3em] opacity-80">Size</div>
            <div className="text-6xl font-bold leading-none">{last.size || '—'}</div>
            <div className="text-2xl mt-2 truncate opacity-95">{last.name}</div>
            <div className="text-xl mt-1 opacity-95">
              {last.status === 'ok' && <>{last.size}: <b>{last.total_scanned}</b> of {last.qty_ordered} · product {last.style_scanned} of {last.style_ordered}</>}
              {last.status === 'over' && <>MORE than ordered: {last.size} {last.total_scanned} of {last.qty_ordered} — counted</>}
              {last.status === 'extra' && <>not on the order — counted as extra</>}
              {last.status === 'unknown' && <>no shop knows this barcode — in box {last.box_label}, a manager looks</>}
              {(last.status === 'extra' || last.status === 'unknown') && onItIsOnOrder && (
                <button onClick={onItIsOnOrder} className="ml-4 px-3 py-1 rounded-lg bg-white/90 hover:bg-white text-red-900 text-base font-semibold align-middle"
                  title="the piece is on the order under another barcode: choose its line, the product gets this barcode">It IS on the order →</button>
              )}
              {last.status === 'no_box' && <>{last.message}</>}
              {last.needs_check && onVerify && last.line_id && (
                <button disabled={busy} onClick={() => onVerify(last.line_id!, last.total_scanned || 0)}
                  className="ml-4 px-3 py-1 rounded-lg bg-white/90 hover:bg-white text-red-900 text-base font-semibold align-middle"
                  title="the count is right — the pieces may go on a cart">Checked — {last.total_scanned} came ✓</button>
              )}
            </div>
            <div className="text-lg mt-1 opacity-80">
              {last.size_min && <>sizes {last.size_min}{last.size_max !== last.size_min ? ` … ${last.size_max}` : ''}</>}
              {(last.waiting || 0) > 0 && <span className="ml-3 font-semibold px-2 py-0.5 rounded bg-black/30">W-F-NOS ×{last.waiting} — a customer waits for this</span>}
            </div>
          </div>
        </div>
      )}
      <div className="flex-none flex justify-end">
        {!typing ? <button onClick={() => setTyping(true)} className="text-xs underline opacity-70 hover:opacity-100">type a barcode</button> : (
          <input autoFocus value={code} onChange={e => setCode(e.target.value)} placeholder="barcode"
            onKeyDown={e => { if (e.key === 'Enter') { onScan(code); setCode(''); setTyping(false); } if (e.key === 'Escape') setTyping(false); }}
            onBlur={() => setTyping(false)} className="w-44 bg-white border border-[#9aa0a6] text-[#111111] rounded px-2 py-1 text-sm" />
        )}
      </div>
    </div>
  );
}

function chipTitle(b: InboundBox): string {
  if (b.status === 'replenished') return `on the shelf${b.replenished_to ? ` (${b.replenished_to})` : ''}`;
  if (b.trip) return `on a cart: trip ${b.trip.trip_no}, ${b.trip.name || b.trip.person || '?'} on ${b.trip.tablet || '?'} (${b.trip.phase})`;
  if (b.unknown) return 'a piece no shop knows — it stays here; a manager decides at the check';
  if (b.needs_check) return 'more than ordered, or not on the order — tap "Checked" on the row when the count is right';
  if (b.ready) return `ready: a cart tablet can take it (${b.pieces || 0} pieces)`;
  return b.to_stock ? 'was Ä — arrived as a size range, stocked now' : b.status;
}

/** the box chips of an inbound; with `actions` (an open scanning session) a complete box is tapped when it went onto
 *  the platform, and + / − add or remove a grey box carrying the same number */
export function BoxChips({ boxes, actions, onAside, onAddBox, onRemoveBox }: {
  boxes: InboundBox[]; actions: boolean;
  onAside?: (b: InboundBox) => void; onAddBox?: (b: InboundBox) => void; onRemoveBox?: (b: InboundBox) => void;
}) {
  if (boxes.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1 mb-4 items-center">
      {boxes.map(b => (
        <span key={b.id} title={chipTitle(b)} className={`inline-flex items-center rounded text-xs font-semibold ${b.to_stock ? 'ring-1 ring-amber-400 ' : ''}${
          b.status === 'replenished' ? 'bg-[#dfe3e8] text-[#8a9099] line-through' :
          b.trip ? 'bg-blue-700 text-white' :
          b.unknown ? 'bg-white border-2 border-red-600 text-red-800' :
          b.needs_check ? 'bg-amber-300 text-black' :
          b.special ? 'bg-purple-800 text-white' :
          b.ready ? 'bg-green-700 text-white' : b.status === 'aside' ? 'bg-green-100 text-green-900 border border-green-600' :
          'bg-white border border-[#9aa0a6] text-[#111111]'}`}>
          <button disabled={!actions || !!b.special || (b.status !== 'complete' && b.status !== 'aside')} onClick={() => onAside?.(b)}
            title={b.special ? `${b.special} box` : b.status === 'complete' && actions ? 'Complete — tap when the box went onto the platform' : b.status} className="px-1.5 py-0.5">
            {boxText(b)}{b.status === 'aside' ? ' ↗' : ''}{b.trip && b.status !== 'replenished' ? ` · ${b.trip.name || b.trip.person || ''}` : ''}
          </button>
          {actions && !b.special && b.status !== 'replenished' && b.status !== 'on_cart' && !b.trip_id && (
            <button onClick={() => onAddBox?.(b)} title="one more grey box with this number" className="px-1 py-0.5 border-l border-black/30 hover:bg-black/20">+</button>
          )}
          {actions && !b.special && b.boxes > 1 && b.status !== 'replenished' && b.status !== 'on_cart' && !b.trip_id && (
            <button onClick={() => onRemoveBox?.(b)} title="one grey box fewer" className="px-1 py-0.5 border-l border-black/30 hover:bg-black/20">−</button>
          )}
        </span>
      ))}
      <span className="text-[#5f6368] text-xs self-center ml-2">boxes: green = ready for a cart tablet · amber = check the count first · purple = Ä/Ö · blue = on a cart (who) · red ring = a piece no shop knows · struck = on the shelf</span>
    </div>
  );
}
